import { useEffect, useState, useCallback, useRef } from 'react';
import { Typography, Divider, Fab, Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';

import PromptField from './PromptField';
import { useConfiguration } from '@/providers/ConfigurationContext';
import { useApi } from '@/providers/ApiContext';
import ChatBox from '@/components/shared/ChatBox';
import SourceDocument from './SourceDocument';
import { addChatItem, getChatItems, updateChatItem } from '@/actions/chat';
import LoadingMask from '@/components/shared/LoadingMask';
import { searchDocs } from '@/actions/search';
import { generateCompletion } from '@/actions/chat';
import { countTokens } from '@/actions/tokenize';
import buildMessage from './buildMessage';
import IncludeHistoryField from '@/components/shared/IncludeHistoryField';
import buildMessages from './buildMessages';
import { MODEL_TYPES } from '@/models/ModelTypes';
import type { ModelConfiguration } from '@/models/modelConfigurations';
import type { Chat as ChatType, ChatItemUpdateRequest } from '@/types/apiTypes';
import type { ChatItem } from '@/types/apiTypes';
import type { Doc } from '@/types/sharedTypes';
import type { EntityId } from '@/types/apiTypes';

interface GenerateResponse {
  generated_text: string;
  finish_reason: string;
  input_tokens: number;
  sources: Doc[];
  include_history?: boolean;
}

interface ChatProps {
  chat: ChatType;
  doc_id: EntityId | null | undefined;
}

function Chat({ chat, doc_id }: ChatProps): React.JSX.Element {
  const configuration = useConfiguration();
  const api = useApi();
  const [prompt, setPrompt] = useState<string>('');
  const [prompt_error, setPromptError] = useState<boolean | string>(false);
  const [chat_history, setChatHistory] = useState<ChatItem[] | null>(null);
  const [source_docs, setSourceDocs] = useState<Doc[]>([]);
  const [generation, setGeneration] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [include_history, setIncludeHistory] = useState<boolean>(true);
  const stopGenerationRef = useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (chat?.entity_id && !chat_history && api) {
      getChatItems(api, chat.entity_id)
        .then((data) => {
          setChatHistory(data);
        })
        .catch((error) => console.log(error));
    }
  }, [chat?.entity_id, chat_history, api]);

  // Reset searching state when chat changes
  useEffect(() => {
    setIsSearching(false);
    stopGenerationRef.current = false;
  }, [chat?.entity_id]);

  // Clear sources when chat changes (don't show historical sources)
  useEffect(() => {
    if (chat?.entity_id) {
      setSourceDocs([]);
    }
  }, [chat?.entity_id]);

  // Function to scroll to bottom of chat content
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, []);

  // Robust scroll to bottom that waits for container to be ready
  const scrollToBottomWhenReady = useCallback(() => {
    const attemptScroll = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight;
        return true;
      }
      return false;
    };

    // Try immediately
    if (attemptScroll()) return;

    // If not ready, try again after a short delay
    setTimeout(() => {
      attemptScroll();
    }, 50);
  }, []);

  // Auto-scroll to bottom when generation updates
  useEffect(() => {
    if (generation) {
      // Use requestAnimationFrame for smooth, responsive scrolling
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [generation, scrollToBottom]);

  // More aggressive scrolling during active generation
  useEffect(() => {
    if (generation && generation.length > 0) {
      // Scroll immediately
      scrollToBottom();

      // Also scroll after a small delay to catch any DOM updates
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [generation, scrollToBottom]);

  // Auto-scroll to bottom when component mounts or chat changes
  useEffect(() => {
    if (chat_history && chat_history.length > 0) {
      // Use a longer delay for initial render to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToBottomWhenReady();
      }, 300);

      // Also try scrolling after a longer delay to catch any late DOM updates
      const backupTimer = setTimeout(() => {
        scrollToBottomWhenReady();
      }, 600);

      return () => {
        clearTimeout(timer);
        clearTimeout(backupTimer);
      };
    }
  }, [chat?.entity_id, chat_history, scrollToBottomWhenReady]);

  // Separate effect for initial chat history load
  useEffect(() => {
    if (chat_history && chat_history.length > 0) {
      // Scroll to bottom when chat history is first loaded
      const timer = setTimeout(() => {
        scrollToBottomWhenReady();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [chat_history, scrollToBottomWhenReady]);

  const addToChatHistory = useCallback(
    async (chat_item: {
      name?: string;
      role: 'user' | 'assistant' | 'system';
      content: string;
      order: number;
      sources?: string;
      finish_reason?: string;
      input_tokens?: number;
      generated_tokens?: number;
      temperature?: number;
      include_history?: boolean;
      [key: string]: unknown;
    }) => {
      if (!api || !chat?.entity_id) return;
      const content_tokens = await countTokens(api, chat_item.content);
      try {
        const resp = await addChatItem(api, chat.entity_id, {
          ...chat_item,
          content_tokens,
        });

        if (chat_item.role === 'assistant') {
          setGeneration(null);
        } else {
          setIsSearching(true);
        }

        setChatHistory((prevHistory) => {
          if (!prevHistory) return [resp];
          console.log('Updating chat history:', {
            previous: prevHistory.length,
            newItem: resp,
            newTotal: prevHistory.length + 1,
          });
          return [...prevHistory, resp];
        });

        return resp;
      } catch (error) {
        console.error('Error adding chat item:', error);
        throw error;
      }
    },
    [chat?.entity_id, api],
  );

  const generateResponse = useCallback(
    async (
      prompt: string,
      chat_history: ChatItem[] | null,
      doc_id: EntityId | null | undefined,
    ): Promise<GenerateResponse> => {
      setGeneration('');
      stopGenerationRef.current = false;

      // Cleanup function to reset stop generation
      const cleanup = () => {
        stopGenerationRef.current = false;
        setIsSearching(false);
        setGeneration(null);
      };

      // Create an AbortController for the HTTP request (only when needed)
      let abortController: AbortController | null = null;

      // Check for stop before searchDocs
      if (stopGenerationRef.current) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      if (!api) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      const doc_query = include_history
        ? prompt +
          '\n' +
          [...(chat_history || [])]
            .reverse()
            .map((chat_line) => chat_line.content)
            .join('\n')
        : prompt;

      const searchResponse = await searchDocs(api, doc_query, 5, [], doc_id || undefined);
      const docs = (searchResponse as unknown as (Doc & { certainty: number; id: string })[]) || [];

      // Check for stop after searchDocs
      if (stopGenerationRef.current) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      // Check for stop before buildMessages
      if (stopGenerationRef.current) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      if (!configuration?.model?.['llm']) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: docs,
        };
      }

      let messages;
      if (include_history) {
        console.log('Building messages with history:', {
          prompt,
          docs,
          chat_history,
        });
        const modelConfig = configuration.model?.['llm'] as ModelConfiguration | undefined;
        if (!modelConfig) {
          cleanup();
          return {
            generated_text: '',
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: docs,
          };
        }
        messages = await buildMessages(
          api,
          modelConfig,
          prompt,
          docs,
          chat_history || [],
        );
        console.log('Built messages with history:', messages);
      } else {
        console.log('Building single message:', { prompt, docs });
        const modelConfig = configuration.model?.['llm'] as ModelConfiguration | undefined;
        if (!modelConfig) {
          cleanup();
          return {
            generated_text: '',
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: docs,
          };
        }
        const message = await buildMessage(
          api,
          modelConfig,
          prompt,
          docs,
        );
        messages = [message];
        console.log('Built single message:', messages);
      }

      const input_tokens = messages.reduce(
        (accumulator, message) => accumulator + message.content_tokens,
        0,
      );

      // Check for stop before generateCompletion
      if (stopGenerationRef.current) {
        cleanup();
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens: 0,
          sources: [],
        };
      }

      // Create AbortController now that we're about to make the HTTP request
      abortController = new AbortController();

      // Update cleanup to abort the request
      const originalCleanup = cleanup;
      const cleanupWithAbort = () => {
        originalCleanup();
        if (abortController) {
          abortController.abort();
        }
      };

      console.log('About to call generateCompletion with:', {
        api: !!api,
        chat_id: chat?.entity_id,
        model: MODEL_TYPES.TEXT,
        messages,
        max_tokens: configuration?.generation?.max_new_tokens,
        temperature: configuration?.generation?.temperature,
        top_p: configuration?.generation?.top_p,
      });

      const completionMessages = messages.map((msg) => ({
        role: msg.role,
        content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content),
      }));

      const response = await generateCompletion(
        api,
        chat?.entity_id,
        MODEL_TYPES.TEXT,
        completionMessages,
        configuration?.generation?.max_new_tokens || 500,
        configuration?.generation?.temperature || 0.7,
        configuration?.generation?.top_p || 1,
        abortController.signal,
      );

      console.log('generateCompletion response:', response);
      console.log('Response status:', response.status);
      console.log('Response body:', response.body);

      // Check for stop after the HTTP request is made
      if (stopGenerationRef.current) {
        cleanupWithAbort();
        setSourceDocs(docs);
        return {
          generated_text: '',
          finish_reason: 'cancelled',
          input_tokens,
          sources: docs,
        };
      }

      if (!response.body) {
        cleanupWithAbort();
        return {
          generated_text: '',
          finish_reason: 'error',
          input_tokens,
          sources: docs,
        };
      }

      const reader = response.body.getReader();
      let line = '',
        finish_reason: string | undefined,
        answer = '';

      console.log('Starting to read response stream...');

      try {
        while (true) {
          // Check if generation should be stopped
          if (stopGenerationRef.current) {
            cleanupWithAbort();
            setSourceDocs(docs);
            return {
              generated_text: answer,
              finish_reason: 'cancelled',
              input_tokens,
              sources: docs,
            };
          }

          // Use Promise.race to make stop checks more responsive
          const readPromise = reader.read();
          const stopCheckPromise = new Promise<{
            done: boolean;
            value: Uint8Array | null;
            cancelled?: boolean;
          }>((resolve) => {
            const checkStop = () => {
              if (stopGenerationRef.current) {
                resolve({ done: true, value: null, cancelled: true });
              } else {
                setTimeout(checkStop, 50); // Check every 50ms
              }
            };
            checkStop();
          });

          const raceResult = await Promise.race([
            readPromise,
            stopCheckPromise,
          ]);
          const done = 'done' in raceResult ? raceResult.done : (raceResult as { done: boolean; value: Uint8Array | null; cancelled?: boolean }).done;
          const value = 'value' in raceResult ? raceResult.value : (raceResult as { done: boolean; value: Uint8Array | null; cancelled?: boolean }).value;
          const cancelled = 'cancelled' in raceResult ? raceResult.cancelled : false;

          if (cancelled) {
            cleanupWithAbort();
            setSourceDocs(docs);
            return {
              generated_text: answer,
              finish_reason: 'cancelled',
              input_tokens,
              sources: docs,
            };
          }

          if (value) {
            line += new TextDecoder().decode(value);
          }
          if (line.endsWith('\n')) {
            const lines = line
              .trim()
              .split('\n')
              .filter((line) => line);
            for (let i = 0; i < lines.length; i++) {
              const data = lines[i].substring(5).trim();
              if (data === '[DONE]') {
                cleanup();
                setSourceDocs(docs);
                return {
                  generated_text: answer,
                  finish_reason: finish_reason || 'stop',
                  input_tokens,
                  sources: docs,
                };
              } else {
                const result = JSON.parse(data) as {
                  choices?: Array<{
                    finish_reason?: string;
                    delta?: { content?: string };
                  }>;
                  generated_text?: string;
                  token?: { text: string };
                };
                if (result?.choices) {
                  if (result?.choices[0]?.finish_reason) {
                    finish_reason = result?.choices[0]?.finish_reason;
                  }
                  const choice = result.choices?.[0];
                  const modelConfig = configuration?.model?.['llm'] as ModelConfiguration | undefined;
                  if (
                    choice?.delta?.content &&
                    modelConfig &&
                    'eos_token' in modelConfig &&
                    Array.isArray(modelConfig.eos_token) &&
                    !modelConfig.eos_token.includes(choice.delta.content)
                  ) {
                    answer += choice.delta.content;

                    setGeneration((prevGeneration) => {
                      const newGeneration =
                        (prevGeneration || '') + (choice.delta?.content || '');
                      // Turn off searching indicator when first token arrives
                      if (!prevGeneration && newGeneration !== '') {
                        setIsSearching(false);
                      }
                      // Scroll to bottom immediately after updating generation
                      setTimeout(() => scrollToBottom(), 0);
                      return newGeneration;
                    });
                  }
                } else {
                  if (!result.generated_text && result.token?.text) {
                    answer += result.token.text;
                    setGeneration((prevGeneration) => {
                      const newGeneration = (prevGeneration || '') + result.token!.text;
                      // Turn off searching indicator when first token arrives
                      if (!prevGeneration && newGeneration !== '') {
                        setIsSearching(false);
                      }
                      // Scroll to bottom immediately after updating generation
                      setTimeout(() => scrollToBottom(), 0);
                      return newGeneration;
                    });
                  }
                }
              }
            }
            line = '';
          }
          if (done) {
            cleanup();
            setSourceDocs(docs);
            return {
              generated_text: answer,
              finish_reason: finish_reason || 'stop',
              input_tokens,
              sources: docs,
            };
          }
        }
      } catch (error) {
        cleanupWithAbort();
        setSourceDocs(docs);

        // Check if the error is due to cancellation
        if ((error as Error).name === 'AbortError') {
          return {
            generated_text: answer,
            finish_reason: 'cancelled',
            input_tokens: 0,
            sources: docs,
          };
        }

        throw error;
      }
    },
    [
      chat?.entity_id,
      configuration?.generation?.max_new_tokens,
      configuration?.generation?.temperature,
      configuration?.generation?.top_p,
      configuration?.model,
      include_history,
      api,
      setIsSearching,
      scrollToBottom,
    ],
  );

  const handlePromptSubmit = useCallback(
    (prompt: string) => {
      // Check if stop was clicked before we even start
      if (stopGenerationRef.current) {
        setGeneration(null);
        setIsSearching(false);
        return;
      }

      // Store the prompt value to ensure it's available
      const currentPrompt = prompt;

      // Clear previous sources when starting a new conversation
      setSourceDocs([]);

      // Immediately clear prompt and disable input to show user their message was sent
      setPrompt('');
      setPromptError(false);

      addToChatHistory({
        name: 'User',
        role: 'user',
        content: currentPrompt,
        order: chat_history?.length || 0,
      })
        .then(() => {
          // Check again after adding user message
          if (stopGenerationRef.current) {
            setGeneration(null);
            setIsSearching(false);
            return;
          }

          // Scroll to bottom after adding user message
          setTimeout(() => {
            scrollToBottom();
          }, 100);

          // console.log(chat_history);
          console.log('Calling generateResponse with prompt:', currentPrompt);
          generateResponse(currentPrompt, chat_history, doc_id)
            .then(async (response) => {
              // Check if generation was cancelled
              if (response.finish_reason === 'cancelled') {
                // Only skip if no content was generated
                if (
                  !response.generated_text ||
                  response.generated_text.trim() === ''
                ) {
                  return;
                }
              }

              console.log(response);
              if (!api) return;
              const generated_tokens = await countTokens(
                api,
                response.generated_text.trim(),
              );
              try {
                await addToChatHistory({
                  name: 'AccQsure',
                  role: 'assistant',
                  content: response.generated_text.trim(),
                  order: (chat_history?.length || 0) + 1,
                  sources: JSON.stringify(response.sources),
                  finish_reason: response.finish_reason,
                  input_tokens: response.input_tokens,
                  generated_tokens,
                  temperature: configuration?.generation?.temperature,
                  include_history: response.include_history,
                });

                // Scroll to bottom after adding assistant response
                setTimeout(() => {
                  scrollToBottom();
                }, 100);
              } catch (error) {
                console.log(error);
                setGeneration(null);
                setIsSearching(false);
              }
            })
            .catch((error) => {
              console.log(error);
              setGeneration(null);
              setIsSearching(false);
            });
        })
        .catch((error) => {
          console.log(error);
          setGeneration(null);
          setIsSearching(false);
        });
    },
    [
      addToChatHistory,
      chat_history,
      configuration?.generation?.temperature,
      generateResponse,
      api,
      scrollToBottom,
      doc_id,
    ],
  );

  const handleUpdateChatLine = useCallback(
    async (chat_item_id: EntityId, values: ChatItemUpdateRequest) => {
      if (!api || !chat?.entity_id) return;
      const resp = await updateChatItem(
        api,
        chat.entity_id,
        chat_item_id,
        values,
      );
      setChatHistory((prevHistory) => {
        if (!prevHistory) return [resp];
        const index = prevHistory.findIndex(
          (line) => line.entity_id === resp.entity_id,
        );
        const new_chat_history = [...prevHistory];
        if (index >= 0) {
          new_chat_history[index] = resp;
        }
        return new_chat_history;
      });
      // Scroll to bottom after updating chat line
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    },
    [chat?.entity_id, api, scrollToBottom],
  );

  if (!chat_history) {
    return <LoadingMask />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        maxHeight: '100%',
        minHeight: 0,
      }}
    >
      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '15px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
          // Ensure no nested scrolling conflicts
          '& *': {
            overflow: 'visible',
          },
        }}
        ref={scrollContainerRef}
      >
        <Grid container spacing={1}>
          <Grid size={12}>
              <ChatBox
              chat_history={chat_history || []}
              generation={generation || undefined}
              isSearching={isSearching}
              updateChatLine={handleUpdateChatLine}
            />
          </Grid>
          {source_docs.length > 0 ? (
            <Box
              sx={{
                margin: '10px',
              }}
            >
              <Grid size={12}>
                <Divider />
              </Grid>{' '}
              <Grid size={12}>
                <Typography variant='h6'>Sources</Typography>{' '}
              </Grid>
              <Grid container spacing={0} size={12}>
                {source_docs.map((doc, i) => {
                  return (
                    <Grid key={`source_doc_${i}`} size={12}>
                      <SourceDocument doc={doc as Doc & { certainty: number; start_line?: number; end_line?: number }} />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : undefined}
        </Grid>
      </Box>

      {/* Fixed Prompt Section at Bottom */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          padding: '15px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: '180px',
          zIndex: 1,
        }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant='caption' sx={{ margin: '10px' }}>
              <b>Disclaimer:</b> The generated output is for assistance only and
              should be reviewed by subject matter experts before being used in
              any product or decision.
            </Typography>
          </Grid>
          <Grid size={12} paddingBottom={'15px'}>
            <Grid container size={12}>
              <Grid size={11}>
                <PromptField
                  value={prompt}
                  error={prompt_error}
                  setValue={setPrompt}
                  setError={setPromptError}
                  onSubmit={() => handlePromptSubmit(prompt)}
                  disabled={generation !== null}
                  generating={generation !== null}
                />
              </Grid>
              <Grid size={1} sx={{ textAlign: 'right' }}>
                {generation !== null ? (
                  <Fab
                    color='primary'
                    aria-label='stop generation'
                    onClick={() => {
                      stopGenerationRef.current = true;
                    }}
                    size='small'
                  >
                    <StopIcon />
                  </Fab>
                ) : (
                  <Fab
                    color='primary'
                    aria-label='send'
                    onClick={() => handlePromptSubmit(prompt)}
                    size='small'
                    disabled={generation !== null}
                  >
                    <SendIcon />
                  </Fab>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12} sx={{ textAlign: 'right' }}>
            <IncludeHistoryField
              disabled={generation !== null}
              value={include_history}
              setValue={setIncludeHistory}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default Chat;

